// systemLogic.js
import { collection, addDoc, updateDoc, deleteDoc, doc, getDoc, query, where, getDocs } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { toast } from 'react-hot-toast';

/**
 * 1. حذف العقار وملفاته نهائياً من Storage و Firestore
 */
export const executeDeleteProject = async (id, logActivity, db, storage) => {
    const loadingToast = toast.loading('جاري حذف العقار وملفاته من السيرفر...');
    try {
        const docId = String(id);
        const projectRef = doc(db, "projects", docId);
        const docSnapshot = await getDoc(projectRef);

        if (docSnapshot.exists()) {
            const projectData = docSnapshot.data();
            // حذف الصور من Storage
            if (projectData.images && Array.isArray(projectData.images)) {
                await Promise.allSettled(projectData.images.map(async (url) => {
                    if (url && url.includes('firebasestorage')) {
                        const imageRef = ref(storage, url);
                        await deleteObject(imageRef);
                    }
                }));
            }
            // حذف البيانات من Firestore
            await deleteDoc(projectRef);
            await logActivity("حذف عقار", `تم حذف العقار: ${projectData.name}`, projectData);
            toast.success("تم حذف العقار بنجاح", { id: loadingToast });
            return true;
        }
    } catch (error) {
        toast.error(`فشل الحذف: ${error.message}`, { id: loadingToast });
        return false;
    }
};

/**
 * 2. حفظ أو تعديل عقار مع رفع الصور تلقائياً
 */
export const executeSaveProject = async (data, logActivity, db, storage) => {
    const loadingToast = toast.loading('جاري معالجة الصور وحفظ العقار...');
    try {
        const newImageUrls = [];
        // رفع الصور الجديدة إن وجدت
        if (data.rawFiles && data.rawFiles.length > 0) {
            for (const file of data.rawFiles) {
                const storageRef = ref(storage, `project_units/${Date.now()}_${file.name}`);
                const snapshot = await uploadBytes(storageRef, file);
                const url = await getDownloadURL(snapshot.ref);
                newImageUrls.push(url);
            }
        }

        const finalImages = [
            ...(data.images || []).filter(url => typeof url === 'string' && !url.startsWith('blob:')),
            ...newImageUrls
        ];

        const projectData = {
            ...data,
            images: finalImages,
            price: Number(data.price),
            area: Number(data.area),
            rawFiles: null, // تنظيف الملفات الخام
            updatedAt: new Date().toISOString()
        };

        if (data.id) {
            await updateDoc(doc(db, "projects", String(data.id)), projectData);
            await logActivity("تعديل عقار", `تعديل بيانات: ${data.name}`, projectData);
        } else {
            await addDoc(collection(db, "projects"), { ...projectData, createdAt: new Date().toISOString() });
            await logActivity("إضافة عقار", `إضافة عقار جديد: ${data.name}`, projectData);
        }

        toast.success("تم حفظ العقار بنجاح", { id: loadingToast });
        return true;
    } catch (error) {
        toast.error(`خطأ: ${error.message}`, { id: loadingToast });
        return false;
    }
};

/**
 * 3. إغلاق صفقة سريع (من صفحة العملاء) مع ترحيل مالي للمشتري والبائع
 */
export const executeCloseDealQuick = async (customer, finalPrice, branch, logActivity, db) => {
    const loadingToast = toast.loading('جاري إغلاق الصفقة وترحيل المالية...');
    try {
        const dealAmount = Number(finalPrice);
        const dateNow = new Date().toISOString().split('T')[0];

        // 1. تحديث العميل
        await updateDoc(doc(db, "customers", String(customer.id)), {
            status: 'تم التنفيذ',
            closedAmount: dealAmount,
            closingDate: new Date().toISOString(),
            closingBranch: branch
        });

        // 2. سجل المبيعات
        const saleDoc = await addDoc(collection(db, "sales"), {
            contractNumber: `MKG-Q-${Date.now().toString().slice(-5)}`,
            customerName: customer.name,
            amount: dealAmount,
            repId: String(customer.repId),
            branch: branch,
            date: dateNow,
            status: 'مكتمل'
        });

        // 3. ترحيل الخزينة
        await addDoc(collection(db, "transactions"), {
            type: 'income',
            title: `إتمام صفقة سريع: ${customer.name}`,
            amount: dealAmount,
            date: dateNow,
            category: 'مبيعات',
            branch: branch,
            saleId: saleDoc.id,
            customerName: customer.name
        });

        await logActivity("إغلاق صفقة", `إتمام سريع للعميل: ${customer.name}`, { customer, finalPrice, branch });
        toast.success("تمت العملية بنجاح", { id: loadingToast });
        return true;
    } catch (error) {
        toast.error(`خطأ: ${error.message}`, { id: loadingToast });
        return false;
    }
};

/**
 * 4. الحذف الذكي (خزينة + مبيعات + إعادة عميل)
 * تم التعديل لإظهار كامل تفاصيل المعاملة في سجل العمليات
 */
export const executeSmartDelete = async (t, logActivity, db, deleteField) => {
    try {
        // بناء نص تفصيلي للعملية قبل حذفها
        const typeArabic = t.type === 'income' ? 'وارد/إيداع' : 'مصروف/سحب';
        const detailMessage = `حذف معاملة مالية:
        - النوع: ${typeArabic}
        - البيان: ${t.title}
        - المبلغ: ${Number(t.amount).toLocaleString()} ج.م
        - الفرع: ${t.branch || 'غير محدد'}
        - التصنيف: ${t.category || 'عام'}`;

        // حذف المالية من Firestore
        await deleteDoc(doc(db, "transactions", String(t.id)));

        // إذا كانت مبيعات، نحذف الأثر المرتبط (عقد البيع وتحديث حالة العميل)
        if (t.category === 'مبيعات' && t.saleId) {
            await deleteDoc(doc(db, "sales", String(t.saleId)));
            if (t.customerName) {
                const q = query(collection(db, "customers"), where("name", "==", t.customerName));
                const snap = await getDocs(q);
                for (const d of snap.docs) {
                    await updateDoc(doc(db, "customers", d.id), {
                        status: 'قيد التنفيذ',
                        closedAmount: deleteField(),
                        closingDate: deleteField()
                    });
                }
            }
        }
        
        // تسجيل النشاط مع التفاصيل الكاملة
        await logActivity("حذف مالي ذكي", detailMessage, t);
        return true;
    } catch (error) {
        throw error;
    }
};

/**
 * 5. حفظ عقد المبيعات المطور (مشتري + بائع)
 */
export const executeSaveContract = async (formData, isEdit, logActivity, db) => {
    const loadingToast = toast.loading('جاري معالجة العقد ماليًا...');
    try {
        const saleData = {
            ...formData,
            contractTotalValue: Number(formData.contractTotalValue),
            customerPaid: Number(formData.customerPaid || 0),
            ownerPaid: Number(formData.ownerPaid || 0),
            updatedAt: new Date().toISOString()
        };

        if (isEdit) {
            await updateDoc(doc(db, "sales", String(formData.id)), saleData);
            await logActivity("تعديل عقد", `تعديل العقد رقم: ${formData.contractNumber}`, saleData);
        } else {
            const saleDoc = await addDoc(collection(db, "sales"), { ...saleData, createdAt: new Date().toISOString() });
            const totalIncome = Number(saleData.customerPaid) + Number(saleData.ownerPaid);
            
            if (totalIncome > 0) {
                await addDoc(collection(db, "transactions"), {
                    type: 'income',
                    title: `تحصيل عقد #${formData.contractNumber}`,
                    amount: totalIncome,
                    date: formData.date,
                    category: 'مبيعات',
                    branch: formData.branch,
                    saleId: saleDoc.id,
                    customerName: formData.customerName
                });
            }
            await logActivity("تسجيل عقد", `إضافة عقد جديد: ${formData.contractNumber}`, saleData);
        }
        toast.success("تم الحفظ والترحيل بنجاح", { id: loadingToast });
        return true;
    } catch (error) {
        toast.error(`خطأ: ${error.message}`, { id: loadingToast });
        return false;
    }
};